import { useReactiveVar } from '@apollo/client'
import { ProjectElementNode } from '@deltek/specification-client/core/Models/productListingDetail'
import {
    getKpPageItemValues,
    updateKpMainLocalStore,
} from '@deltek/specification-client/core/Utilities/localstorageUtils'
import { userInfo } from 'api/graphQL/reactiveVars/user'
import { isReactiveContentTeamFromStudioPage } from 'api/graphQL/reactiveVars/workspaceContent'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useShowRightMostPane } from '../context/RightMostPaneContext'
import { RightMostPaneTabs } from '../enums/RightMostPaneTabs'

interface UseRightMostPaneProps {
    selectedProjectElementNode?: ProjectElementNode | null
}

export const useRightMostPane = ({
    selectedProjectElementNode,
}: UseRightMostPaneProps) => {
    const { projectId } = useParams<{ projectId: string }>()
    const { currentUserInfo } = useReactiveVar(userInfo)
    const loggedInUserId = currentUserInfo?.kpUserId ?? ''
    const isOMorSMMPage = isReactiveContentTeamFromStudioPage()?.value
    const { showRightMostPane } = useShowRightMostPane()

    const getStorageKey = (id?: string) => `currentPane_${id ?? ''}`

    const getOMSMMVisitCount = (savedData: any): number => {
        const visitCount = savedData?.['visitCount']
        return parseInt(
            Array.isArray(visitCount) ? visitCount[0] : visitCount ?? '0',
            10
        )
    }

    const getSavedPane = (): RightMostPaneTabs | undefined => {
        const savedData = getKpPageItemValues({
            currentUserId: loggedInUserId,
            pageKey: getStorageKey(projectId),
        })

        const savedPane = savedData?.['rightpanel']
        const savedPageType = savedData?.['rightpanelPageType']
        const visitCount = getOMSMMVisitCount(savedData)

        const isValidTab = Object.values(RightMostPaneTabs).includes(
            savedPane as RightMostPaneTabs
        )

        if (!isValidTab) return undefined

        const currentPageType = String(isOMorSMMPage)

        // First visit to OM/SMM page, no saved pane
        if (currentPageType === 'true' && savedPageType !== 'true')
            return undefined

        // Returning to OM/SMM page, use saved pane if visitCount > 0
        if (currentPageType === 'true' && savedPageType === 'true') {
            return visitCount > 0 ? (savedPane as RightMostPaneTabs) : undefined
        }

        // Not OM/SMM page, return saved pane if valid
        if (currentPageType === 'false' && savedPageType === 'false') {
            return savedPane as RightMostPaneTabs
        }

        return undefined
    }

    const defaultPane = RightMostPaneTabs.AskDela

    const [currentPane, setCurrentPane] = useState<RightMostPaneTabs | ''>(
        () => {
            return getSavedPane() ?? defaultPane
        }
    )

    /**
     * Clears currentPane when the rightmost pane is hidden.
     */
    useEffect(() => {
        if (!showRightMostPane) setCurrentPane('')
    }, [showRightMostPane])

    /**
     * Updates currentPane based on project, element node, or page type changes..
     */
    useEffect(() => {
        if (!selectedProjectElementNode) return
        setCurrentPane((prev) => {
            if (prev === '') return ''
            return getSavedPane() ?? defaultPane
        })
    }, [selectedProjectElementNode?.id, projectId, isOMorSMMPage])

    /**
     * Syncs pane state and visit count to local storage
     */
    useEffect(() => {
        if (!projectId) return

        updateKpMainLocalStore({
            currentUserId: loggedInUserId,
            pageKey: getStorageKey(projectId),
            itemKey: 'rightpanel',
            itemValue: currentPane,
        })

        updateKpMainLocalStore({
            currentUserId: loggedInUserId,
            pageKey: getStorageKey(projectId),
            itemKey: 'rightpanelPageType',
            itemValue: String(isOMorSMMPage),
        })

        if (isOMorSMMPage) {
            const savedData = getKpPageItemValues({
                currentUserId: loggedInUserId,
                pageKey: getStorageKey(projectId),
            })

            const visitCount = getOMSMMVisitCount(savedData)
            updateKpMainLocalStore({
                currentUserId: loggedInUserId,
                pageKey: getStorageKey(projectId),
                itemKey: 'visitCount',
                itemValue: (visitCount + 1).toString(),
            })
        }
    }, [currentPane, projectId, isOMorSMMPage])

    return { currentPane, changeTab: setCurrentPane }
}
